import { Search } from 'lucide-react';
import { useTranslation } from '../../../context/LanguageContext';
import type { MediaFilter } from '../../../hooks/useMediaLibrary';

interface MediaFiltersProps {
  filter: MediaFilter;
  onChange: (f: MediaFilter) => void;
  totalCount: number;
}

type TypeOption = MediaFilter['type'];
type StatusOption = MediaFilter['status'];

export function MediaFilters({ filter, onChange, totalCount }: Readonly<MediaFiltersProps>) {
  const { t } = useTranslation();

  const typeOptions: { value: TypeOption; label: string }[] = [
    { value: 'all', label: t('media.filterAll') },
    { value: 'image', label: t('media.filterImages') },
    { value: 'video', label: t('media.filterVideos') },
  ];

  const statusOptions: { value: StatusOption; label: string }[] = [
    { value: 'active', label: t('media.filterActive') },
    { value: 'archived', label: t('media.filterArchived') },
    { value: 'all', label: t('media.filterAllStatus') },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Type toggle */}
      <div className="flex rounded-lg border border-slate-700 bg-slate-800 p-0.5 gap-0.5">
        {typeOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange({ ...filter, type: opt.value })}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              filter.type === opt.value
                ? 'bg-violet-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Status select */}
      <select
        value={filter.status}
        onChange={(e) => onChange({ ...filter, status: e.target.value as StatusOption })}
        className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs text-white focus:border-violet-500 focus:outline-none"
      >
        {statusOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Search */}
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
        <input
          type="text"
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
          placeholder={t('media.searchPlaceholder')}
          className="w-full rounded-lg border border-slate-700 bg-slate-800 pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
        />
      </div>

      <p className="text-xs text-slate-500 ml-auto">{totalCount} files</p>
    </div>
  );
}
